import React, {useState} from 'react';
import {License, ParentLicense} from '../../schema';
import {DateTime} from "luxon";
import DateTimeFormatter from "../../models/formatter/DateTimeFormatter";
import If from "../utils/If";
import PrimaryButton from "../../ui/PrimaryButton";
import AddSubLicenseDlg from './AddSubLicenseDlg';
import TextButton from "../../ui/TextButton";
import Avatar from "../../ui/Avatar";
import Dialog from "../../ui/modal/Dialog";
import gql from "graphql-tag";
import {useMutation} from "@apollo/client";
import {handleError, handleSuccess} from "../../utils/Apollo";

type Props = {
  license: ParentLicense;
  refetchListQuery?: string[]
}
export default function SubLicense({license, refetchListQuery}: Props) {
  const activeSubLicenses = license.subLicenses.filter((subLicense) => subLicense.active);
  const availableSeats = license.seats - activeSubLicenses.length;
  const [showAddDlg, setShowAddDlg] = useState(false);
  const [showRevokeDlg, setShowRevokeDlg] = useState(false);
  const [currentLicense, setCurrentLicense] = useState<License | null>(null);

  const [revokeLicense] = useMutation(mutation);

  const onConfirmRevoke = () => {
    revokeLicense({
      variables: {id: currentLicense?.id, parentLicenseId: license.id},
      refetchQueries: refetchListQuery
    }).then(() => {
      handleSuccess(`License revoked`);
      setShowRevokeDlg(false);
    }, (apolloError) => handleError(apolloError));
  };

  const onRevokeClick = (x: License) => {
    setCurrentLicense(x);
    setShowRevokeDlg(true);
  }


  return (
    <div className="py-6 px-6 rounded-xl mt-3 bg-gray-light">
      <div className="flex flex-col justify-between">
        <div className="flex justify-between">
          <h2 className="text-xl text-gray-dark font-medium mt-2">{license.type?.name}</h2>
          <If condition={availableSeats > 0}>
            <PrimaryButton onClick={() => setShowAddDlg(true)}> Add trainer</PrimaryButton>
          </If>
        </div>
        <div className="flex flex-row justify-between mt-8">
          <div>
            <span className="font-bold">Total seats:</span>
            <span className="ml-2">{license.seats}</span>
          </div>
          <div>
            <span className="font-bold">Available seats:</span>
            <span className="ml-2">{availableSeats}</span>
          </div>
          <div>
            <span className="font-bold">Expires at:</span>
            <span className="ml-2">{DateTimeFormatter.format('en', DateTime.fromISO(license.expiresAt))}</span>
          </div>
        </div>
        {activeSubLicenses.length > 0 && (
          <div className="mt-8 flex flex-col space-y-3 bg-white p-4 rounded-xl">
            {activeSubLicenses.map((subLicense) => (
              <div className="flex flex-row justify-between border-b last:border-b-0 border-gray-light pb-3 last:pb-0"
                   key={subLicense.id}>
                <span className="pt-1 flex"><Avatar name={subLicense.licensee.name}
                                                    url={subLicense.licensee.avatar ? subLicense.licensee.avatar : undefined}
                                                    className="w-6 h-6 rounded-full mr-2 -mt-0.5"/>{subLicense.licensee.name}</span>
                <TextButton short={true} onClick={() => onRevokeClick(subLicense)}>Revoke</TextButton>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog show={showRevokeDlg} actionBtnTitle="Revoke" onClose={() => setShowRevokeDlg(false)}
              title={`Revoke license from ${currentLicense?.licensee.name}`} onConfirm={() => onConfirmRevoke()}
              closeBtnTitle="Cancel">
        <p>
          You are going to revoke license from trainer <b>{currentLicense?.licensee.name}</b>. This action cannot be
          undone but you can easily
          grant the license again.
        </p>
      </Dialog>
      <AddSubLicenseDlg show={showAddDlg} setShowDialog={setShowAddDlg} parentLicenseId={license.id}
                        licensedTrainersIds={activeSubLicenses.map((x) => x.licensee.trainer?.id || '')}
                        refetchQueries={refetchListQuery} categoryName={license.category?.name}/>
    </div>
  );
}

const mutation = gql`
  mutation RevokeMutation($parentLicenseId: ID!, $id: ID!)
  {
    license: revokeSubLicense(parentLicenseId: $parentLicenseId, licenseId: $id) {
      id
    }
  }
`;
